<template>
  <div class="artifact-version-detail">
    <div v-if="!versionId" class="empty-state">
      <a-empty description="请选择一个制品版本查看详情" />
    </div>

    <div v-else class="detail-content">
      <div v-if="loading" class="loading-state">
        <a-spin size="large" />
      </div>

      <template v-else-if="dataSource">
        <div class="detail-body">
          <!-- 基本信息 -->
          <div class="info-group">
            <div class="info-group__title">基本信息</div>
            <a-descriptions size="small">
              <a-descriptions-item label="版本ID">
                <div class="info-item-content">
                  {{ dataSource.version_id }}
                  <a-button type="link" size="small" :title="getCopyStatus('version_id')" @click="copyText(dataSource.version_id, 'version_id')">
                    <a-icon :type="getCopyIcon('version_id')" />
                  </a-button>
                </div>
              </a-descriptions-item>
              <a-descriptions-item label="版本号">
                <div class="info-item-content">
                  <a-tag color="blue">{{ dataSource.image_name }}</a-tag>
                  <a-button type="link" size="small" :title="getCopyStatus('version')" @click="copyText(dataSource.image_name, 'version')">
                    <a-icon :type="getCopyIcon('version')" />
                  </a-button>
                </div>
              </a-descriptions-item>
              <a-descriptions-item label="Git标签">
                <div class="info-item-content">
                  <a-tag v-if="dataSource.git_tag" color="green">{{ dataSource.git_tag }}</a-tag>
                  <span v-else class="no-data">--</span>
                  <a-button v-if="dataSource.git_tag" type="link" size="small" :title="getCopyStatus('tags')" @click="copyText(dataSource.git_tag, 'tags')">
                    <a-icon :type="getCopyIcon('tags')" />
                  </a-button>
                  <a-button v-if="dataSource.git_tag" type="link" size="small" title="在GitLab中查看标签" @click="openGitLab('tags', dataSource.git_tag)">
                    <a-icon type="link" />
                  </a-button>
                </div>
              </a-descriptions-item>
              <a-descriptions-item label="命名空间">
                {{ dataSource.namespace }}
              </a-descriptions-item>
              <a-descriptions-item label="制品仓库">
                {{ dataSource.service_name }}
              </a-descriptions-item>
              <a-descriptions-item label="创建人">
                <div>
                  <user-profiles v-if="dataSource.creator" :value="[dataSource.creator]" avatar />
                  <span v-else class="no-data">--</span>
                </div>
              </a-descriptions-item>
              <a-descriptions-item label="Commit ID">
                <div class="info-item-content">
                  <a-tag color="orange">{{ dataSource.commit_id }}</a-tag>
                  <a-button type="link" size="small" :title="getCopyStatus('commit')" @click="copyText(dataSource.commit_id, 'commit')">
                    <a-icon :type="getCopyIcon('commit')" />
                  </a-button>
                  <a-button type="link" icon="link" size="small" title="在GitLab中查看提交" @click="openGitLab('commit', dataSource.commit_id)" />
                </div>
              </a-descriptions-item>
              <a-descriptions-item label="制品地址">
                <div class="artifact-url-content">
                  <div class="url-text">{{ dataSource.image_url }}</div>
                  <a-button type="link" size="small" :title="getCopyStatus('artifactUrl')" @click="copyText(dataSource.image_url, 'artifactUrl')">
                    <a-icon :type="getCopyIcon('artifactUrl')" />
                  </a-button>
                </div>
              </a-descriptions-item>
            </a-descriptions>
          </div>

          <!-- 提交信息 -->
          <div class="info-group">
            <div class="info-group__title">提交信息</div>
            <div class="commit-message">
              <pre>{{ dataSource.commit_message || '暂无提交信息' }}</pre>
            </div>
          </div>

          <!-- 关联任务/缺陷 -->
          <div v-if="dataSource.issues?.length" class="info-group">
            <div class="info-group__title">关联任务/缺陷</div>
            <div class="issues-list">
              <div v-for="issue in dataSource.issues" :key="issue.key" class="issue-item" @click="openIssue(issue)">
                <div class="issue-item__icon">
                  <a-icon :type="getIssueTypeIcon(issue.issue_type)" />
                </div>
                <div class="issue-item__key-and-title">
                  <span class="issue-key">{{ issue.key }}</span>
                  <a-tooltip :title="issue.title">
                    <span class="issue-title">{{ issue.title }}</span>
                  </a-tooltip>
                </div>
              </div>
            </div>
          </div>

          <!-- 关联发布评审 -->
          <div v-if="dataSource.relation_releases?.length" class="info-group">
            <div class="info-group__title">关联发布评审</div>
            <div v-for="(release, index) in dataSource.relation_releases" :key="index" class="pipeline-card">
              <div class="meta-item">
                <span class="meta-label">标题:</span>
                <a class="custom-link" :href="releaseLink(release)" target="_blank">{{ release.release_title }}</a>
              </div>
              <div class="meta-item">
                <span class="meta-label">所属流程:</span>
                <a class="custom-link" :href="workflowLink(release)" target="_blank">{{ release.workflow_title || '--' }}</a>
              </div>
              <div class="meta-item">
                <span class="meta-label">状态:</span>
                <span class="meta-value">
                  <a-badge :status="releaseStatus[release.release_status].status" :text="releaseStatus[release.release_status].label" />
                </span>
              </div>
              <div class="meta-item">
                <span class="meta-label">创建人:</span>
                <span class="meta-value">
                  <user-profiles v-if="release.release_creator" :value="[release.release_creator]" avatar />
                </span>
              </div>
              <div class="meta-item">
                <span class="meta-label">创建时间:</span>
                <span class="meta-value">{{ release.created_time || '--' }}</span>
              </div>
            </div>
          </div>

          <!-- 关联构建流水线 -->
          <div v-if="dataSource.pipeline_record" class="info-group">
            <div class="info-group__title">关联构建流水线</div>
            <div class="pipeline-card" @click="openPipeline(dataSource.pipeline_record)">
              <div class="meta-item">
                <span class="meta-label">触发方式:</span>
                <a-tag :color="getTriggerModeColor(dataSource.pipeline_record.trigger_mode)" size="small">
                  {{ getTriggerModeText(dataSource.pipeline_record.trigger_mode) }}
                </a-tag>
              </div>
              <div class="meta-item">
                <span class="meta-label">分支:</span>
                <a-tag color="blue" size="small">{{ dataSource.pipeline_record.branch || '--' }}</a-tag>
              </div>
              <div class="meta-item">
                <span class="meta-label">持续时间:</span>
                <span class="meta-value">{{ dataSource.pipeline_record.duration || '--' }}</span>
              </div>
              <div class="meta-item">
                <span class="meta-label">触发人:</span>
                <span class="meta-value">
                  <user-profiles v-if="dataSource.pipeline_record.creator" :value="[dataSource.pipeline_record.creator]" avatar />
                </span>
              </div>
              <div class="meta-item">
                <span class="meta-label">触发时间:</span>
                <span class="meta-value">{{ dataSource.pipeline_record.created_time || '--' }}</span>
              </div>
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { releaseStatus } from '@/utils/dictionary';
import { artifactVersionDetail } from '@/api/system/artifact';

// 常量配置
const COPY_STATES = {
  version_id: { name: '版本ID', status: 'initial' },
  version: { name: '版本号', status: 'initial' },
  tags: { name: 'Git标签', status: 'initial' },
  commit: { name: 'Commit ID', status: 'initial' },
  artifactUrl: { name: '制品地址', status: 'initial' }
};

const ISSUE_TYPE_CONFIG = {
  task: { icon: 'check-square', color: 'blue', text: '任务' },
  issue: { icon: 'exclamation-circle', color: 'red', text: '问题' },
  story: { icon: 'book', color: 'green', text: '需求' },
  bug: { icon: 'bug', color: 'orange', text: '缺陷' }
};

const TRIGGER_MODE_CONFIG = {
  1: { color: 'green', text: '自动触发' },
  2: { color: 'blue', text: '手动触发' }
};

export default {
  name: 'ArtifactVersionDetail',
  props: {
    versionId: {
      type: [String, Number],
      default: null
    }
  },
  data() {
    return {
      releaseStatus,
      loading: false,
      dataSource: null,
      copyStates: { ...COPY_STATES }
    };
  },
  computed: {
    releaseLink() {
      return record => {
        const link = this.$router.resolve({
          name: 'page:project:workflow:release:detail',
          params: { project_id: record.project_id },
          query: { releaseId: record.release_id }
        });
        return link.href;
      };
    },
    workflowLink() {
      return record => {
        const link = this.$router.resolve({
          name: 'page:project:workflow:detail',
          params: { project_id: record.project_id },
          query: { workflowId: record.workflow_id }
        });
        return link.href;
      };
    }
  },
  watch: {
    versionId: {
      handler(newId) {
        if (newId) {
          this.getArtifactVersionDetail(newId);
        } else {
          this.dataSource = null;
        }
      },
      immediate: true
    }
  },
  methods: {
    async getArtifactVersionDetail(versionId) {
      this.loading = true;
      this.dataSource = null;
      try {
        const res = await artifactVersionDetail({ id: versionId });
        this.dataSource = res.data;
      } finally {
        this.loading = false;
      }
    },

    async copyText(textToCopy, typeKey) {
      const state = this.copyStates[typeKey];
      if (!state) {
        this.$message.error(`复制失败: 未知的类型 "${typeKey}"`);
        return;
      }

      const onCopySuccess = () => {
        // 重置所有成功状态
        Object.keys(this.copyStates).forEach(key => {
          if (this.copyStates[key].status === 'success') {
            this.copyStates[key].status = 'initial';
          }
        });
        state.status = 'success';
        this.$message.success(`${state.name}已复制`);
        setTimeout(() => {
          state.status = 'initial';
        }, 2000);
      };

      try {
        if (navigator.clipboard) {
          await navigator.clipboard.writeText(textToCopy);
          onCopySuccess();
        } else {
          // 降级方案
          const textArea = document.createElement('textarea');
          textArea.value = textToCopy;
          textArea.style.position = 'fixed';
          textArea.style.top = '0';
          textArea.style.left = '0';
          document.body.appendChild(textArea);
          textArea.focus();
          textArea.select();
          document.execCommand('copy');
          document.body.removeChild(textArea);
          onCopySuccess();
        }
      } catch (err) {
        this.$message.error(`复制失败: ${err}`);
      }
    },

    getCopyStatus(typeKey) {
      const state = this.copyStates[typeKey];
      return state ? (state.status === 'success' ? '已复制' : `复制${state.name}`) : '复制';
    },

    getCopyIcon(typeKey) {
      const state = this.copyStates[typeKey];
      return state ? (state.status === 'success' ? 'check' : 'copy') : 'copy';
    },

    openGitLab(type, value) {
      if (!this.dataSource?.http_url) {
        this.$message.warning('无法获取GitLab项目地址');
        return;
      }
      window.open(`${this.dataSource.http_url.replace(/\.git$/, '')}/-/${type}/${value}`);
    },

    getIssueTypeIcon(type) {
      return ISSUE_TYPE_CONFIG[type]?.icon || 'question-circle';
    },

    openIssue(issue) {
      const routeName = issue.issue_type === 'task' ? 'page:project:task:detail' : 'page:project:quality:issue:detail';

      const query = issue.issue_type === 'task' ? { taskId: issue.id } : { issueId: issue.id };

      const route = this.$router.resolve({
        name: routeName,
        query,
        params: { project_id: issue.project_id }
      });
      window.open(route.href, '_blank');
    },

    openPipeline(pipeline) {
      const route = this.$router.resolve({
        name: 'page:project:cicd:pipeline:detail',
        query: { recordId: pipeline.record_id }
      });
      window.open(route.href, '_blank');
    },

    getTriggerModeColor(mode) {
      return TRIGGER_MODE_CONFIG[mode]?.color || 'default';
    },

    getTriggerModeText(mode) {
      return TRIGGER_MODE_CONFIG[mode]?.text || '未知';
    }
  }
};
</script>

<style lang="less" scoped>
.artifact-version-detail {
  height: 100%;
  display: flex;
  flex-direction: column;

  .empty-state,
  .loading-state {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .loading-state {
    flex-direction: column;
    gap: 16px;
  }

  .detail-content {
    height: 100%;
    display: flex;
    flex-direction: column;
    overflow: hidden;

    .detail-body {
      flex: 1;
      padding: 16px;
      overflow-y: auto;

      .info-group {
        background-color: #fff;
        margin-bottom: 16px;
        border-radius: 16px;

        &:last-child {
          margin-bottom: 0;
        }

        &__title {
          margin-bottom: 12px;
          color: rgba(0, 0, 0, 0.85);
          font-size: 14px;
          font-weight: 500;
          position: relative;
          padding-left: 12px;

          &::before {
            content: '';
            position: absolute;
            left: 0;
            top: 50%;
            transform: translateY(-50%);
            width: 4px;
            height: 16px;
            background-color: #1890ff;
            border-radius: 2px;
          }
        }
      }

      .info-item-content {
        display: flex;
        align-items: center;
        gap: 4px;
        flex-wrap: wrap;

        .ant-btn {
          padding: 0;
          height: auto;
          line-height: 1;
          min-width: 16px;

          .anticon {
            font-size: 12px;
          }
        }
      }

      .artifact-url-content {
        display: flex;
        align-items: flex-start;
        gap: 8px;

        .url-text {
          flex: 1;
          word-break: break-all;
          white-space: pre-wrap;
          font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
          line-height: 1.4;
          padding: 8px 12px;
          font-size: 12px;
          color: #333;
          background: #f5f5f5;
          border-radius: 4px;
        }

        .ant-btn {
          padding: 0;
          height: auto;
          line-height: 1;
          flex-shrink: 0;
        }
      }

      .commit-message {
        pre {
          margin: 0;
          white-space: pre-wrap;
          word-wrap: break-word;
          font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
          font-size: 12px;
          line-height: 1.5;
          color: #333;
          background: #f5f5f5;
          padding: 12px;
          border-radius: 4px;
        }
      }

      .issues-list {
        display: flex;
        flex-direction: column;
        gap: 8px;
      }

      .issue-item {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 12px;
        border-radius: 6px;
        border: 1px solid #e8e8e8;
        background-color: #fff;
        cursor: pointer;
        transition: all 0.3s;

        &:hover {
          border-color: #1890ff;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.09);
        }

        &__icon {
          font-size: 18px;
          color: #1890ff;
        }

        .issue-key {
          font-weight: 500;
          color: #333;
        }

        .issue-title {
          flex: 1;
          font-size: 13px;
          color: #666;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
      }

      .pipeline-card {
        display: flex;
        align-items: center;
        gap: 16px;
        padding: 12px;
        border-radius: 6px;
        border: 1px solid #e8e8e8;
        background-color: #fff;
        cursor: pointer;
        transition: all 0.3s;

        &:hover {
          border-color: #1890ff;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.09);
        }

        .meta-item {
          display: flex;
          flex: 1;
          align-items: center;
          gap: 4px;
          white-space: nowrap;

          .meta-label {
            color: rgba(0, 0, 0, 0.45);
            font-size: 12px;
          }

          .meta-value {
            color: rgba(0, 0, 0, 0.85);
            font-size: 12px;
          }

          .ant-tag {
            margin: 0;
          }
        }
      }
    }
  }
}

.no-data {
  color: #999;
  font-size: 12px;
}

:deep(.ant-descriptions-item) {
  padding-bottom: 16px;
  display: flex;

  &:last-child {
    padding-bottom: 0;
  }

  .ant-descriptions-item-label {
    color: rgba(0, 0, 0, 0.45);
    font-size: 13px;
    line-height: 28px;
    text-align: right;
    padding-right: 4px;
    width: 96px;
    white-space: nowrap;
    flex: none;
  }

  .ant-descriptions-item-content {
    color: rgba(0, 0, 0, 0.85);
    font-size: 13px;
    line-height: 28px;
    flex: 1;
    min-width: 0;
    word-break: break-all;
  }
}

:deep(.ant-descriptions-row) {
  display: block;
}
</style>
